import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as CallTypeAPI from 'api/CallType';
import { dispatch } from 'store';
import { TYPE_OF_FIELD_IN_COLUMN } from 'utils/scorecard';
import { openSnackbar } from 'store/reducers/snackbar';

const displayMessage = ({ type, msg }) => {
  dispatch(
    openSnackbar({
      open: true,
      message: msg ? msg : type === 'success' ? 'Success' : 'Something went wrong',
      variant: 'alert',
      alert: {
        color: type || 'success'
      },
      close: false
    })
  );
};

const initialState = {
  pagination: {
    pageSize: 10,
    currentPage: 1,
    totalRecords: null
  },
  column: [
    { title: '#', key: 'id', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
    { title: 'Name', key: 'name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
    { title: 'Scope', key: 'scope', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
    { title: 'Subscription Plans', key: 'subscription_plan_type', field_type: TYPE_OF_FIELD_IN_COLUMN.ARRAY, width: 120 },
    { title: 'Action', key: 'actions', field_type: TYPE_OF_FIELD_IN_COLUMN.ACTIONS, width: 120 }
  ],
  data: null,
  customerCallType: {
    pagination: {
      pageSize: 10,
      currentPage: 1,
      totalRecords: null
    },
    column: [
      { title: '#', key: 'id', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
      { title: 'Name', key: 'agent_core_call_type.name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'Scope', key: 'agent_core_call_type.scope', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      {
        title: 'Subscription Plans',
        key: 'agent_core_call_type.subscription_plan_type',
        field_type: TYPE_OF_FIELD_IN_COLUMN.ARRAY,
        width: 120
      },
      { title: 'Status', key: 'status', field_type: TYPE_OF_FIELD_IN_COLUMN.STATUS, width: 120 },
      { title: 'Action', key: 'actions', field_type: TYPE_OF_FIELD_IN_COLUMN.ACTIONS, width: 120 }
    ],
    data: null,
    allPublicCallTypes: []
  },
  status: '',
  ui: {
    loading: false,
    msg: ''
  }
};

const getCallTypeData = createAsyncThunk('callType/getCallTypeData', async ({ params, signal }) => {
  const res = await CallTypeAPI.getCallTypeData(params, signal);
  return res?.data?.data;
});

const createCallType = createAsyncThunk('callType/createCallType', async (params) => {
  try {
    const res = await CallTypeAPI.createCallType(params);
    displayMessage({ type: 'success', msg: 'Created successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.response?.data?.message?.name?.toString() || error?.message });
  }
});

const updateCallType = createAsyncThunk('callType/updateCallType', async (params) => {
  try {
    const res = await CallTypeAPI.updateCallType(params);
    displayMessage({ type: 'success', msg: 'Updated successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteCallType = createAsyncThunk('callType/deleteCallType', async (id) => {
  try {
    const res = await CallTypeAPI.deleteCallType(id);
    displayMessage({ type: 'success', msg: 'Deleted successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

//Customer
const getCustomerCallTypeData = createAsyncThunk('callType/getCustomerCallTypeData', async ({ params, signal }) => {
  const res = await CallTypeAPI.getCustomerCallTypeData(params, signal);
  return res?.data?.data;
});

const createCustomerCallType = createAsyncThunk('callType/createCustomerCallType', async (params) => {
  try {
    const res = await CallTypeAPI.createCustomerCallType(params);
    displayMessage({ type: 'success', msg: 'Attached successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.response?.data?.message?.agent_core_call_type?.toString() || error?.message });
  }
});

const updateCustomerCallType = createAsyncThunk('callType/updateCustomerCallType', async (params) => {
  try {
    const res = await CallTypeAPI.updateCustomerCallType(params);
    displayMessage({ type: 'success', msg: 'Updated successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteCustomerCallType = createAsyncThunk('callType/deleteCustomerCallType', async (id) => {
  try {
    const res = await CallTypeAPI.deleteCustomerCallType(id);
    displayMessage({ type: 'success', msg: 'Deleted successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const getAllPublicCallTypes = createAsyncThunk('callType/getAllPublicCallTypes', async () => {
  const res = await CallTypeAPI.getAllPublicCallTypes();
  return res?.data?.data;
});

const CallTypeSlice = createSlice({
  name: 'callType',
  initialState: initialState,
  reducers: {
    resetState: () => {
      return initialState;
    },
    setCallTypeCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload;
    },
    setCallTypePageSize: (state, action) => {
      state.pagination.pageSize = action.payload;
    },

    //Customer
    setCustomerCallTypeCurrentPage: (state, action) => {
      state.customerCallType.pagination.currentPage = action.payload;
    },
    setCustomerCallTypePageSize: (state, action) => {
      state.customerCallType.pagination.pageSize = action.payload;
    }
  },
  extraReducers: {
    [getAllPublicCallTypes.fulfilled]: (state, action) => {
      state.customerCallType.allPublicCallTypes = action.payload;
    },
    [getCallTypeData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getCallTypeData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.pagination.totalRecords = action.payload?.count;
      state.data = action.payload?.results || [];
    },
    [getCallTypeData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [createCallType.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [createCallType.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [createCallType.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [updateCallType.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [updateCallType.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      const updatedData = state.data.map((item) => {
        if (item.id === action.payload.id) {
          return action.payload;
        }
        return item;
      });
      state.data = updatedData;
    },

    [updateCallType.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [deleteCallType.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [deleteCallType.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [deleteCallType.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [getCustomerCallTypeData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getCustomerCallTypeData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.customerCallType.pagination.totalRecords = action.payload?.count;
      state.customerCallType.data = action.payload?.results || [];
    },
    [getCustomerCallTypeData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [createCustomerCallType.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [createCustomerCallType.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [createCustomerCallType.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [updateCustomerCallType.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [updateCustomerCallType.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [createCustomerCallType.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [deleteCustomerCallType.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [deleteCustomerCallType.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [deleteCustomerCallType.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    }
  }
});

const { resetState, setCallTypeCurrentPage, setCallTypePageSize, setCustomerCallTypeCurrentPage, setCustomerCallTypePageSize } =
  CallTypeSlice.actions;

export default CallTypeSlice.reducer;

export {
  resetState,
  setCallTypeCurrentPage,
  setCallTypePageSize,
  getCallTypeData,
  createCallType,
  updateCallType,
  deleteCallType,
  //Customer
  setCustomerCallTypeCurrentPage,
  setCustomerCallTypePageSize,
  getAllPublicCallTypes,
  getCustomerCallTypeData,
  createCustomerCallType,
  updateCustomerCallType,
  deleteCustomerCallType
};
